import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, Pressable, StyleSheet } from 'react-native';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { useIsFocused, useNavigation } from '@react-navigation/native';
import { useTheme, ThemeColors } from '../../context/ThemeContext';
import { AnimatePresence, MotionDiv } from '../motion/Motion';
import { EASE_OUT, MOTION } from '../../utils/motionPresets';
import {
  PortalPage,
  flattenResults,
  highlightParts,
  portalPages,
  rememberRecent,
  searchPortal,
} from '../../utils/portalSearch';

interface HeaderSearchProps {
  /** Phones: an icon in the header that opens the search as a sheet over the top of the page. */
  compact: boolean;
  /** Show the Ctrl K / ⌘K hint in the box (it still works without); off where space is tight. */
  showShortcut?: boolean;
  /** CXC registration is open and this child can enter, so its page can be found too. */
  cxcOpen: boolean;
}

// The web build reaches the browser through `any` - this project compiles without the DOM lib.
const web: any = globalThis as any;
const RECENT_KEY = 'parentPortal.search.recent';
const isMac = /Mac|iPhone|iPad/.test(web.navigator?.platform ?? '');

/** Pages opened from the search on this browser. A convenience only: losing it costs nothing. */
const loadRecent = (): string[] => {
  try {
    const saved = JSON.parse(web.localStorage?.getItem(RECENT_KEY) ?? '[]');
    return Array.isArray(saved) ? saved.filter((r: unknown) => typeof r === 'string') : [];
  } catch {
    return [];
  }
};
const saveRecent = (recent: string[]) => {
  try {
    web.localStorage?.setItem(RECENT_KEY, JSON.stringify(recent));
  } catch {
    // Private windows and blocked storage: the search still works, it just forgets.
  }
};

/**
 * The header's search (approved design A, 2 Nov 2026): every page a parent can open, by name or
 * by everyday words (utils/portalSearch). Pages only - switching child is the header's own
 * switcher. Ctrl K / ⌘K from anywhere, ↑ ↓ to move, Enter to open, Esc to close.
 */
export function HeaderSearch({ compact, showShortcut = true, cxcOpen }: HeaderSearchProps) {
  const navigation = useNavigation<any>();
  const isScreenFocused = useIsFocused();
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);

  const inputRef = useRef<any>(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [active, setActive] = useState(0);
  const [recent, setRecent] = useState<string[]>(loadRecent);

  const groups = useMemo(
    () => searchPortal(query, { pages: portalPages({ cxcOpen }), recent }),
    [query, cxcOpen, recent],
  );
  const results = useMemo(() => flattenResults(groups), [groups]);

  // A new search starts at its best match.
  useEffect(() => setActive(0), [query]);

  // Every page has its own header, and pages further back stay mounted: re-read the recent
  // list on opening, so a page returned to shows what was opened from the others meanwhile.
  useEffect(() => {
    if (open) setRecent(loadRecent());
  }, [open]);

  // Ctrl K / ⌘K opens the search from anywhere - but only the header of the page on screen
  // listens; pages further back in the stack keep theirs mounted.
  useEffect(() => {
    if (!isScreenFocused || !web.document) return;
    const onKeyDown = (event: any) => {
      if ((event.ctrlKey || event.metaKey) && String(event.key).toLowerCase() === 'k') {
        event.preventDefault();
        setOpen(true);
        inputRef.current?.focus();
      }
    };
    web.document.addEventListener('keydown', onKeyDown);
    return () => web.document.removeEventListener('keydown', onKeyDown);
  }, [isScreenFocused]);

  const close = () => {
    setOpen(false);
    setQuery('');
    inputRef.current?.blur();
  };

  const choose = (page: PortalPage | undefined) => {
    if (!page) return;
    const next = rememberRecent(recent, page.route);
    setRecent(next);
    saveRecent(next);
    // Like the sidebar: a page opened from here shows no leftover "Back to Home".
    navigation.navigate(page.route, { backTo: undefined });
    close();
  };

  const onKeyPress = (event: any) => {
    const key = event.nativeEvent?.key;
    if (key === 'ArrowDown' || key === 'ArrowUp') {
      event.preventDefault?.();
      if (!results.length) return;
      const step = key === 'ArrowDown' ? 1 : -1;
      setActive(current => (current + step + results.length) % results.length);
    } else if (key === 'Escape') {
      // First Esc clears what was typed; the next one closes.
      if (query) setQuery('');
      else close();
    }
  };

  const field = (
    <View style={[styles.field, focused && styles.fieldFocused, compact && styles.fieldCompact]}>
      <FeatherIcon name="search" size={17} color={colors.headerText} />
      <TextInput
        ref={inputRef}
        value={query}
        onChangeText={text => {
          setQuery(text);
          setOpen(true);
        }}
        onFocus={() => {
          setFocused(true);
          setOpen(true);
        }}
        onBlur={() => {
          setFocused(false);
          if (!compact) setOpen(false);
        }}
        onKeyPress={onKeyPress}
        onSubmitEditing={() => choose(results[active])}
        placeholder={showShortcut || compact ? 'Search fees, homework…' : 'Search…'}
        placeholderTextColor={colors.headerTextMuted}
        accessibilityLabel="Search the parent portal"
        autoFocus={compact}
        style={styles.input}
      />
      {query ? (
        <TouchableOpacity onPress={() => setQuery('')} accessibilityLabel="Clear search" style={styles.clear}>
          <FeatherIcon name="x" size={15} color={colors.headerTextMuted} />
        </TouchableOpacity>
      ) : !compact && showShortcut ? (
        <View style={styles.kbd}>
          <Text style={styles.kbdText}>{isMac ? '⌘K' : 'Ctrl K'}</Text>
        </View>
      ) : null}
    </View>
  );

  const list = (
    <SearchResultsList
      groups={groups}
      query={query}
      active={active}
      compact={compact}
      styles={styles}
      onHover={setActive}
      onChoose={choose}
    />
  );

  if (compact) {
    return (
      <>
        <TouchableOpacity style={styles.iconButton} onPress={() => setOpen(true)} accessibilityLabel="Search">
          <FeatherIcon name="search" size={18} color={colors.headerText} />
        </TouchableOpacity>
        <AnimatePresence>
          {open && (
            <MotionDiv
              key="search-sheet"
              style={sheetLayer}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
            >
              <MotionDiv style={sheetBackdrop} onClick={close} aria-hidden />
              <MotionDiv
                style={sheetPanel}
                initial={{ y: -24 }}
                animate={{ y: 0 }}
                exit={{ y: -24 }}
                transition={MOTION.softSpring}
              >
                <View style={styles.sheetTop}>
                  {field}
                  <TouchableOpacity onPress={close} style={styles.cancel}>
                    <Text style={styles.cancelText}>Cancel</Text>
                  </TouchableOpacity>
                </View>
                <View style={styles.sheetResults}>{list}</View>
              </MotionDiv>
            </MotionDiv>
          )}
        </AnimatePresence>
      </>
    );
  }

  return (
    <View style={styles.wrap}>
      {field}
      <AnimatePresence>
        {open && (
          <MotionDiv
            key="search-results"
            style={dropdownPosition}
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.18, ease: EASE_OUT }}
            // Keeps focus in the box while a result is pressed, so the press lands.
            onMouseDown={event => event.preventDefault()}
          >
            <View style={styles.panel}>{list}</View>
          </MotionDiv>
        )}
      </AnimatePresence>
    </View>
  );
}

function SearchResultsList({ groups, query, active, compact, styles, onHover, onChoose }: {
  groups: ReturnType<typeof searchPortal>;
  query: string;
  active: number;
  compact: boolean;
  styles: ReturnType<typeof createStyles>;
  onHover: (index: number) => void;
  onChoose: (page: PortalPage) => void;
}) {
  if (!groups.length) {
    return (
      <Text style={styles.empty}>
        Nothing matches “{query.trim()}”. Try fees, homework or canteen.
      </Text>
    );
  }

  let index = 0;
  return (
    <View>
      {groups.map(group => (
        <View key={group.title}>
          <Text style={styles.groupTitle}>{group.title.toUpperCase()}</Text>
          {group.pages.map(page => {
            const i = index++;
            const isActive = i === active;
            return (
              <Pressable
                key={page.route}
                onHoverIn={() => onHover(i)}
                onPress={() => onChoose(page)}
                style={[styles.row, isActive && styles.rowActive]}
                accessibilityRole="button"
              >
                <View style={[styles.tile, { backgroundColor: page.tint }]}>
                  <FeatherIcon name={page.icon} size={17} color={page.color} />
                </View>
                <View style={styles.rowText}>
                  <Highlighted text={page.title} query={query} style={styles.rowTitle} markStyle={styles.mark} />
                  <Highlighted text={page.subtitle} query={query} style={styles.rowSubtitle} markStyle={styles.mark} />
                </View>
                {isActive && !compact && <Text style={styles.enterHint}>Enter ↵</Text>}
              </Pressable>
            );
          })}
        </View>
      ))}
      {!compact && (
        <View style={styles.footer}>
          <Text style={styles.footerText}>↑ ↓ to move</Text>
          <Text style={styles.footerText}>Enter to open</Text>
          <Text style={styles.footerText}>Esc to close</Text>
        </View>
      )}
    </View>
  );
}

/** The matched part of a result picked out like a highlighter pen. */
function Highlighted({ text, query, style, markStyle }: { text: string; query: string; style: any; markStyle: any }) {
  return (
    <Text style={style} numberOfLines={1}>
      {highlightParts(text, query).map((part, i) => (
        <Text key={i} style={part.match ? markStyle : undefined}>{part.text}</Text>
      ))}
    </Text>
  );
}

// Plain CSS for the MotionDivs (DOM divs on web).
const dropdownPosition = {
  position: 'absolute',
  top: 50,
  left: 0,
  width: 440,
  maxWidth: 'calc(100vw - 32px)',
  zIndex: 1000,
  transformOrigin: 'top left',
};
const sheetLayer = { position: 'fixed', top: 0, right: 0, bottom: 0, left: 0, zIndex: 2000 };
const sheetBackdrop = {
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  backgroundColor: 'rgba(3, 33, 88, 0.45)',
};
const sheetPanel = { position: 'relative' };

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  wrap: {
    flexGrow: 1,
    // Gives way well before the page title does, down to its minimum: on a tablet the title
    // was cut to "Fees & paym…" while the box kept spare width.
    flexShrink: 8,
    minWidth: 160,
    maxWidth: 380,
    marginHorizontal: 16,
  },
  field: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 42,
    borderRadius: 12,
    paddingHorizontal: 12,
    backgroundColor: colors.headerControl,
    borderWidth: 1,
    borderColor: colors.headerControlBorder,
  },
  // The box brightens and glows in the brand blue while it has focus.
  fieldFocused: {
    backgroundColor: 'rgba(255, 255, 255, 0.18)',
    borderColor: 'rgba(255, 255, 255, 0.6)',
    shadowColor: colors.headerAccent,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.9,
    shadowRadius: 10,
  },
  fieldCompact: {
    flex: 1,
    height: 44,
  },
  input: {
    flex: 1,
    minWidth: 0,
    marginLeft: 9,
    fontSize: 14,
    color: colors.headerText,
    // The box shows its own focus; the browser's outline would double it.
    outlineStyle: 'none',
  } as any,
  clear: {
    padding: 4,
  },
  kbd: {
    borderWidth: 1,
    borderColor: colors.headerControlBorder,
    borderRadius: 6,
    paddingHorizontal: 6,
    paddingVertical: 1,
  },
  kbdText: {
    fontSize: 11,
    fontWeight: '600',
    color: colors.headerTextMuted,
  },
  iconButton: {
    width: 38,
    height: 38,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.headerControl,
  },
  panel: {
    backgroundColor: colors.surface,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: colors.border,
    padding: 8,
    shadowColor: '#032158',
    shadowOffset: { width: 0, height: 18 },
    shadowOpacity: 0.18,
    shadowRadius: 40,
  },
  sheetTop: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingTop: 12,
    paddingBottom: 12,
    backgroundColor: colors.headerTo,
  },
  cancel: {
    marginLeft: 10,
    paddingVertical: 8,
  },
  cancelText: {
    color: colors.headerText,
    fontSize: 14,
    fontWeight: '600',
  },
  sheetResults: {
    backgroundColor: colors.surface,
    padding: 8,
    borderBottomLeftRadius: 18,
    borderBottomRightRadius: 18,
  },
  groupTitle: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.4,
    color: colors.textMuted,
    paddingHorizontal: 10,
    paddingTop: 6,
    paddingBottom: 4,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 10,
  },
  rowActive: {
    backgroundColor: colors.primaryLight,
  },
  tile: {
    width: 34,
    height: 34,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  rowText: {
    flex: 1,
    minWidth: 0,
  },
  rowTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: colors.textPrimary,
  },
  rowSubtitle: {
    fontSize: 12,
    color: colors.textSecondary,
    marginTop: 1,
  },
  mark: {
    backgroundColor: '#FDE68A',
    color: '#422006',
    borderRadius: 3,
  },
  enterHint: {
    fontSize: 11,
    fontWeight: '700',
    color: colors.primary,
    marginLeft: 8,
  },
  empty: {
    fontSize: 13,
    color: colors.textSecondary,
    paddingHorizontal: 10,
    paddingVertical: 16,
  },
  footer: {
    flexDirection: 'row',
    gap: 14,
    borderTopWidth: 1,
    borderTopColor: colors.divider,
    marginTop: 6,
    paddingHorizontal: 10,
    paddingTop: 8,
    paddingBottom: 4,
  },
  footerText: {
    fontSize: 11,
    color: colors.textMuted,
  },
});
